// src/pages/InboxPage.tsx

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useAuth, useI18n, useTask, useNotifications } from '@react/hooks';
import { Card, CardHeader, CardTitle, CardContent, Badge } from '@react/components';
import type { Notification } from '@core/types/notifications';

function isOpen(status?: string): boolean {
  if (!status) return true;
  return !status.includes('COMPLETED') && !status.includes('CANCELLED');
}

function isOverdue(dueDate: any): boolean {
  if (!dueDate) return false;
  const date = dueDate.seconds ? new Date(dueDate.seconds * 1000) : new Date(dueDate);
  return date.getTime() < Date.now();
}

function formatTime(ts: any): string {
  if (!ts) return '';
  const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  if (isNaN(date.getTime())) return '';
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString();
}

function statusVariant(s?: string) {
  if (!s) return 'secondary' as const;
  if (s.includes('BLOCKED')) return 'error' as const;
  if (s.includes('IN_PROGRESS') || s.includes('STARTED')) return 'primary' as const;
  if (s.includes('COMPLETED')) return 'success' as const;
  return 'secondary' as const;
}

export const InboxPage = observer(() => {
  const auth = useAuth();
  const { t } = useI18n();
  const tasks = useTask();
  const notificationsStore = useNotifications();
  const [notificationItems, setNotificationItems] = useState<Notification[]>([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  useEffect(() => {
    tasks.ListMyTasks({
      pagination: { cursor: '', limit: 20, direction: 'forward' },
      sort: [],
    });
    if (auth.userId) {
      setNotificationsLoading(true);
      notificationsStore.ListNotificationsByUser(auth.userId)
        .then(() => setNotificationItems([...notificationsStore.items]))
        .finally(() => setNotificationsLoading(false));
    }
  }, []);

  const actionItems = tasks.items
    .filter(i => isOpen(i.status) && (isOverdue(i.due_date) || i.priority?.includes('CRITICAL') || i.priority?.includes('HIGH')))
    .slice(0, 5);

  const unread = notificationItems.filter(n => !n.read_at);
  const hasContent = actionItems.length > 0 || notificationItems.length > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">
          {t('inbox.title', { name: auth.user?.username || t('common.user') })}
        </h1>
        <p className="mt-1 text-muted-foreground">{t('inbox.subtitle')}</p>
      </div>

      {/* Requires Your Action */}
      {actionItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t('inbox.requiresAction')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {actionItems.map((item) => (
                <li key={item.entity?.id}>
                  <Link
                    to={`/tasks/${item.entity?.id}`}
                    className="flex items-center justify-between gap-3 py-3 -mx-2 px-2 rounded hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge variant={statusVariant(item.status)} size="sm">
                          {item.status?.replace(/_/g, ' ').toLowerCase() ?? t('common.unknown')}
                        </Badge>
                        {isOverdue(item.due_date) && (
                          <Badge variant="error" size="sm">{t('inbox.overdue')}</Badge>
                        )}
                      </div>
                    </div>
                    {item.due_date && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTime(item.due_date)}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Activity Feed */}
      {notificationsLoading || tasks.isLoading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : !hasContent ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-lg font-medium text-foreground">{t('inbox.empty.title')}</p>
            <p className="mt-2 text-sm text-muted-foreground">{t('inbox.empty.description')}</p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/tasks/new" className="text-sm text-primary hover:underline">{t('inbox.empty.createTask')}</Link>
              <Link to="/chats" className="text-sm text-primary hover:underline">{t('inbox.empty.openChats')}</Link>
              <Link to="/documents/new" className="text-sm text-primary hover:underline">{t('inbox.empty.createDoc')}</Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('inbox.feed')}</CardTitle>
              {unread.length > 0 && (
                <Badge variant="primary" size="sm">
                  {t('inbox.unreadCount', { count: unread.length })}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <ul className="divide-y divide-border">
              {notificationItems.slice(0, 15).map((n) => (
                <li key={n.entity?.id} className={`py-3 ${!n.read_at ? '' : 'opacity-60'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground line-clamp-1">{n.subject}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{n.content}</p>
                    </div>
                    {n.created_at && (
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatTime(n.created_at)}
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
});

export default InboxPage;
